// src/components/payment/SubscriptionPlans.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../data/supabaseClient';
import { AuthContext } from '../Provider';
import { useToast } from '../hooks/ToastContext';
import { useTranslation } from 'react-i18next';
import Spinner from '../common/Spinner';
import './SubscriptionPlans.css';
import ChatV2 from '../Chat/ChatV2';

const SubscriptionPlans = () => {
  const { t } = useTranslation('common');
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  
  const [userSubscription, setUserSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [softLaunchClaimed, setSoftLaunchClaimed] = useState(false);
  const [showClaimSuccess, setShowClaimSuccess] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch subscription plans from database to get current Stripe price IDs
        const { data: plansData, error: plansError } = await supabase
          .from('subscription_plans')
          .select('*')
          .order('price');
          
        if (plansError) {
          throw plansError;
        }
        
        if (plansData && plansData.length > 0) {
          setPlans(plansData);
          console.log('Plans loaded:', plansData);
        }
        
        // If user is logged in, fetch current subscription
        if (user) {
          console.log('Fetching subscription data for user:', user.id);
          
          // Get user profile including soft launch status
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('email, subscription_status, plan_type, soft_launch_basic_claimed, basic_claim_date')
            .eq('id', user.id)
            .single();
            
          if (profileError) {
            console.error('Error fetching profile:', profileError);
          } else {
            console.log('User profile:', profileData);
            // Set soft launch claimed status
            const claimed = profileData?.soft_launch_basic_claimed || false;
            setSoftLaunchClaimed(claimed);
            console.log('Soft launch Basic claimed status:', claimed);
          }
          
          // Check if user has an active subscription
          if (profileData?.subscription_status === 'active') {
            console.log('User has active subscription, fetching details...');
            
            // Get subscription details
            const { data: subscriptionData, error: subscriptionError } = await supabase
              .from('user_subscriptions')
              .select('*, subscription_plans(*)')
              .eq('user_id', user.id)
              .eq('status', 'active')
              .maybeSingle();
              
            if (subscriptionError) {
              console.error('Error fetching subscription:', subscriptionError);
            } else if (subscriptionData) {
              console.log('Found active subscription:', subscriptionData);
              setUserSubscription(subscriptionData);
            } else {
              console.log('No active subscription found in user_subscriptions table');
              
              // If no subscription record found but profile says active, create a dummy one
              // This handles cases where profile was updated but no subscription record exists
              if (profileData?.subscription_status === 'active' && profileData?.plan_type) {
                console.log('Creating dummy subscription from profile data');
                
                // Find the plan that matches the user's plan_type
                const matchingPlan = plansData.find(plan => 
                  plan.name.toLowerCase() === profileData.plan_type.toLowerCase()
                );
                
                if (matchingPlan) {
                  // Create a dummy subscription object
                  const dummySubscription = {
                    id: 'dummy-' + Date.now(),
                    user_id: user.id,
                    plan_id: matchingPlan.id,
                    status: 'active',
                    subscription_plans: matchingPlan,
                    current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
                    cancel_at_period_end: false
                  };
                  
                  console.log('Created dummy subscription:', dummySubscription);
                  setUserSubscription(dummySubscription);
                }
              }
            }
          } else {
            console.log('User does not have an active subscription');
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        addToast({
          type: 'error',
          title: t('errors.data_load_error'),
          description: t('payments.subscription_load_error')
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    
    // Check for successful subscription redirect
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('success') === 'true') {
      addToast({
        type: 'success',
        title: t('payments.success_title'),
        description: t('payments.success_description')
      });
      
      // Remove query parameters
      navigate(location.pathname, { replace: true });
      
      // Refresh subscription data
      fetchData();
    } else if (searchParams.get('canceled') === 'true') {
      addToast({
        type: 'info',
        title: t('payments.canceled_title'),
        description: t('payments.canceled_description')
      });
      
      // Remove query parameters
      navigate(location.pathname, { replace: true });
    }
  }, [user, t, addToast, location, navigate]);
  
  // Function to claim Basic plan for free during soft launch
  const handleClaimBasicFree = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    if (softLaunchClaimed) {
      addToast({
        type: 'info',
        title: t('payments.soft_launch.already_claimed_title'),
        description: t('payments.soft_launch.already_claimed_description')
      });
      return;
    }
    
    try {
      setIsClaiming(true);
      
      // Call the database function to claim Basic plan
      const { data, error } = await supabase.rpc('claim_basic_plan_free');
      
      if (error) {
        throw new Error(error.message);
      }
      
      if (data.success) {
        // Update local state
        setSoftLaunchClaimed(true);
        setShowClaimSuccess(true);
        
        // Track the claim action
        await supabase.from('soft_launch_analytics').insert({
          user_id: user.id,
          action: 'claim_button_clicked',
          metadata: {
            timestamp: new Date().toISOString(),
            user_agent: navigator.userAgent,
            page_url: window.location.href
          }
        });
        
        addToast({
          type: 'success',
          title: t('payments.soft_launch.claim_success_title'),
          description: t('payments.soft_launch.claim_success_description')
        });
        
        // Refresh the page to show updated subscription status
        setTimeout(() => {
          window.location.reload();
        }, 2000);
        
      } else {
        throw new Error(data.error || 'Failed to claim Basic plan');
      }
      
    } catch (error) {
      console.error('Error claiming Basic plan:', error);
      addToast({
        type: 'error',
        title: t('payments.soft_launch.claim_failed_title'),
        description: error.message || t('payments.soft_launch.claim_failed_description')
      });
    } finally {
      setIsClaiming(false);
    }
  };
  
  const handleSubscribe = async (plan) => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    // During soft launch, disable Premium and Business subscriptions
    if (plan.name === 'Premium' || plan.name === 'Business') {
      addToast({
        type: 'info',
        title: t('payments.soft_launch.coming_soon_toast_title'),
        description: t('payments.soft_launch.coming_soon_toast_description', { plan: plan.name })
      });
      return;
    }
    
    if (!plan.stripe_price_id) {
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: 'Invalid price ID for this plan'
      });
      return;
    }
    
    try {
      setIsProcessing(true);
      
      // Call Supabase function to create checkout session
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          priceId: plan.stripe_price_id,
          returnUrl: window.location.href
        }
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      // Redirect to Stripe Checkout
      window.location.href = data.sessionUrl;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('payments.checkout_error')
      });
      setIsProcessing(false);
    }
  };

  const handleCancelSubscription = async (cancelImmediately) => {
    if (!userSubscription) return;
    
    if (!window.confirm(
      cancelImmediately 
        ? 'Are you sure you want to cancel your subscription immediately? This will end your access to premium features right away.'
        : 'Are you sure you want to cancel your subscription? You will still have access to premium features until the end of the current billing period.'
    )) {
      return; // User canceled the confirmation dialog
    }
    
    try {
      setIsProcessing(true);
      
      // First, check if this is a dummy subscription (created from profile data when no subscription record exists)
      const isDummy = userSubscription.id?.toString().startsWith('dummy-');
      
      if (isDummy) {
        console.log('Handling cancellation for dummy subscription');
        // For dummy subscriptions, we just need to update the profile
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            subscription_status: cancelImmediately ? 'inactive' : 'active', // Keep active until "period end"
            plan_type: cancelImmediately ? 'free' : userSubscription.subscription_plans?.name.toLowerCase() || 'basic'
          })
          .eq('id', user.id);
          
        if (profileError) {
          throw new Error('Failed to update subscription status: ' + profileError.message);
        }
        
        addToast({
          type: 'success',
          title: 'Subscription Canceled',
          description: cancelImmediately 
            ? 'Your subscription has been canceled immediately.'
            : 'Your subscription will be canceled at the end of the current period.'
        });
        
        // Reload to show updated status
        setTimeout(() => window.location.reload(), 2000);
        return;
      }
      
      // For real subscriptions, use the subscription ID
      const subscriptionId = userSubscription.subscription_id || userSubscription.stripe_subscription_id;
      
      if (!subscriptionId) {
        throw new Error('Subscription ID not found');
      }
      
      console.log('Canceling subscription with ID:', subscriptionId);
      
      // Call function to cancel subscription
      try {
        // First attempt to use the Edge Function
        const { data, error } = await supabase.functions.invoke('cancel-subscription', {
          body: {
            cancelImmediately,
            subscriptionId
          }
        });
        
        if (error) {
          throw new Error(error.message);
        }
        
        addToast({
          type: 'success',
          title: 'Subscription Canceled',
          description: data?.message || 'Your subscription has been canceled successfully.'
        });
      } catch (edgeFunctionError) {
        console.error('Edge function error:', edgeFunctionError);
        console.log('Falling back to database function...');
        
        // Fall back to direct database function if edge function fails
        const { data: dbFunctionData, error: dbFunctionError } = await supabase.rpc('cancel_subscription', {
          p_user_id: user.id,
          p_cancel_immediately: cancelImmediately
        });
        
        if (dbFunctionError) {
          console.error('Database function error:', dbFunctionError);
          throw new Error('Failed to cancel subscription using all available methods.');
        }
        
        addToast({
          type: 'success',
          title: 'Subscription Canceled',
          description: dbFunctionData?.message || 'Your subscription has been canceled successfully.'
        });
      }
      
      // Reload the page to show updated subscription status
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      
    } catch (error) {
      console.error('Error canceling subscription:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: 'Failed to cancel subscription: ' + (error.message || 'Unknown error')
      });
      setIsProcessing(false);
    }
  };

    // Removed handleManageSubscription as it's not being used
  
  if (loading) {
    return (
      <div className="SubP-loading-container">
        <Spinner size="medium" />
        <p>{t('app.loading')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="SubP-subscription-plans-container">
        
        {/* Soft Launch Success Banner - Show for all users who got the basic plan */}
        {user && softLaunchClaimed && (
          <div className="SubP-promotional-banner SubP-claimed">
            <div className="SubP-banner-content">
              <h2 className="SubP-banner-title">🎉 {t('payments.soft_launch.banner_claimed_title') || 'Basic Plan Activated!'}</h2>
              <p className="SubP-banner-description">
                {t('payments.soft_launch.banner_claimed_description') || 'You now have access to Basic plan features including package delivery, ad-free experience, and more posts per month!'}
              </p>
            </div>
          </div>
        )}
        

        
        {/* Show current plan summary box - works for both subscribed and free users */}
        <div className={`SubP-current-subscription ${userSubscription ? 'SubP-active' : ''}`}>
          
          <div className="SubP-plan-header">
            <h3 className="SubP-current-plan-title">
              {t('payments.current_plan')}: {userSubscription ? 
                (userSubscription.subscription_plans?.name || 'Premium Plan') : 
                'Free Plan'}
            </h3>
            
            {userSubscription && (
              <p className="SubP-plan-period">
                {userSubscription.cancel_at_period_end 
                  ? t('payments.expires_on', { 
                      date: new Date(userSubscription.current_period_end).toLocaleDateString() 
                    })
                  : t('payments.renews_on', { 
                      date: new Date(userSubscription.current_period_end).toLocaleDateString() 
                    })
                }
              </p>
            )}
          </div>
          
          {/* Show current plan features summary */}
          <div className="SubP-features-summary">
            <h4 className="SubP-features-title">
              {t('payments.your_plan_features')}
            </h4>
            
            <div className="SubP-features-grid">
              {(() => {
                const currentPlan = userSubscription 
                  ? plans.find(p => p.id === userSubscription.plan_id || 
                                 p.name === userSubscription.subscription_plans?.name)
                  : plans.find(p => p.name === 'Free');
                  
                if (!currentPlan || !currentPlan.features) return null;
                
                const featuresToShow = [
                  { 
                    name: currentPlan.features.max_posts_per_month === 9999 
                      ? t('payments.feature_items.unlimited_posts')
                      : t('payments.feature_items.posts_per_month', { count: currentPlan.features.max_posts_per_month }),
                    value: currentPlan.features.max_posts_per_month === 9999 
                      ? t('payments.feature_values.unlimited') 
                      : currentPlan.features.max_posts_per_month
                  },
                  { 
                    name: currentPlan.features.max_chats_per_month === 9999 
                      ? t('payments.feature_items.unlimited_chats')
                      : t('payments.feature_items.chats_per_month', { count: currentPlan.features.max_chats_per_month }),
                    value: currentPlan.features.max_chats_per_month === 9999 
                      ? t('payments.feature_values.unlimited') 
                      : currentPlan.features.max_chats_per_month
                  },
                  { 
                    name: t('payments.feature_items.route_matching', { count: currentPlan.features.route_match_radius_km }),
                    value: `${currentPlan.features.route_match_radius_km}km`
                  },
                  { 
                    name: currentPlan.features.max_groups === 9999 
                      ? t('payments.feature_items.join_unlimited_groups')
                      : t('payments.feature_items.join_groups', { count: currentPlan.features.max_groups }),
                    value: currentPlan.features.max_groups === 9999 
                      ? t('payments.feature_values.unlimited') 
                      : currentPlan.features.max_groups
                  },
                  { 
                    name: t('payments.feature_items.ad_free_experience'),
                    value: currentPlan.features.ad_free 
                      ? t('payments.feature_values.yes') 
                      : t('payments.feature_values.no')
                  },
                  { 
                    name: t('payments.feature_items.package_delivery'),
                    value: currentPlan.features.package_delivery 
                      ? t('payments.feature_values.yes') 
                      : t('payments.feature_values.no')
                  }
                ];
                
                return featuresToShow.map((feature, index) => (
                  <div key={index} className="SubP-feature-item">
                    <span className="SubP-feature-icon">
                      {feature.value === t('payments.feature_values.yes') || 
                       feature.value === t('payments.feature_values.unlimited') || 
                       parseInt(feature.value) > 5 
                        ? '✓' 
                        : feature.value === t('payments.feature_values.no') ? '✗' : ''}
                    </span>
                    <span className="SubP-feature-text">{feature.name}: <strong>{feature.value}</strong></span>
                  </div>
                ));
              })()}
            </div>
          </div>
          
          {/* Subscription management buttons */}
          {userSubscription && !userSubscription.cancel_at_period_end && (
            <div className="SubP-cancel-buttons-container">
              <button 
                onClick={() => handleCancelSubscription(false)}
                className="SubP-cancel-subscription-button"
                disabled={isProcessing}
              >
                {isProcessing ? <Spinner size="small" /> : t('payments.cancel_at_period_end')}
              </button>
            </div>
          )}
        </div>
        
        <div className="SubP-plans-grid">
          {plans.filter(plan => plan.name !== 'Free').map((plan) => (
            <div 
              key={plan.id} 
              className={`SubP-plan-card ${userSubscription && 
                (userSubscription.plan_id === plan.id || 
                 userSubscription.subscription_plans?.name === plan.name) 
                ? 'SubP-current-plan' : ''}`}
            >
              {userSubscription && 
                (userSubscription.plan_id === plan.id || 
                 userSubscription.subscription_plans?.name === plan.name) && (
                <div className="SubP-current-plan-badge">
                  {t('payments.current_plan')}
                </div>
              )}
              
              <h3 className="SubP-plan-name">
                {plan.name === 'Free' && '🆓'}
                {plan.name === 'Basic' && '💡'}
                {plan.name === 'Premium' && '⭐'}
                {plan.name === 'Business' && '🚀'}
                {plan.name}
              </h3>
              
              <p className={`SubP-plan-price ${plan.name === 'Free' ? 'SubP-free-plan-price' : ''}`}>
                {parseFloat(plan.price).toFixed(2)} / {t(`payments.${plan.interval}`)}
              </p>
              
              <p className="SubP-plan-description">
                {plan.description}
              </p>
              
              <div className="SubP-plan-features">
                {plan.features && (
                  <>
                    {/* Posts & Messages */}
                    <div className="SubP-feature-category">
                      <h4 className="SubP-category-title">{t('payments.plan_feature_categories.posts_messages')}</h4>
                      <div className="SubP-feature-item">
                        <span className="SubP-feature-icon SubP-included">✓</span>
                        <span className="SubP-feature-name">
                          {plan.features.max_posts_per_month === 9999 
                            ? t('payments.feature_items.unlimited_posts') 
                            : t('payments.feature_items.posts_per_month', { count: plan.features.max_posts_per_month })}
                        </span>
                      </div>
                      <div className="SubP-feature-item">
                        <span className="SubP-feature-icon SubP-included">✓</span>
                        <span className="SubP-feature-name">
                          {plan.features.max_chats_per_month === 9999 
                            ? t('payments.feature_items.unlimited_chats') 
                            : t('payments.feature_items.chats_per_month', { count: plan.features.max_chats_per_month })}
                        </span>
                      </div>
                    </div>

                    {/* Usage Limits */}
                    <div className="SubP-feature-category">
                      <h4 className="SubP-category-title">{t('payments.plan_feature_categories.usage_limits')}</h4>
                      <div className="SubP-feature-item">
                        <span className="SubP-feature-icon SubP-included">✓</span>
                        <span className="SubP-feature-name">
                          {plan.features.max_groups === 9999 
                            ? t('payments.feature_items.join_unlimited_groups') 
                            : t('payments.feature_items.join_groups', { count: plan.features.max_groups })}
                        </span>
                      </div>
                      <div className="SubP-feature-item">
                        <span className="SubP-feature-icon SubP-included">✓</span>
                        <span className="SubP-feature-name">
                          {plan.features.max_vehicles === 9999 
                            ? t('payments.feature_items.unlimited_vehicles') 
                            : t('payments.feature_items.vehicles', { count: plan.features.max_vehicles })}
                        </span>
                      </div>
                      <div className="SubP-feature-item">
                        <span className="SubP-feature-icon SubP-included">✓</span>
                        <span className="SubP-feature-name">
                          {t('payments.feature_items.route_matching', { count: plan.features.route_match_radius_km })}
                        </span>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="SubP-feature-category">
                      <h4 className="SubP-category-title">{t('payments.plan_feature_categories.features')}</h4>
                      <div className="SubP-feature-item">
                        <span className={`SubP-feature-icon ${plan.features.package_delivery ? 'SubP-included' : 'SubP-excluded'}`}>
                          {plan.features.package_delivery ? '✓' : '✕'}
                        </span>
                        <span className="SubP-feature-name">
                          {t('payments.feature_items.package_delivery')}
                        </span>
                      </div>
                      <div className="SubP-feature-item">
                        <span className={`SubP-feature-icon ${plan.features.ad_free ? 'SubP-included' : 'SubP-excluded'}`}>
                          {plan.features.ad_free ? '✓' : '✕'}
                        </span>
                        <span className="SubP-feature-name">
                          {t('payments.feature_items.ad_free_experience')}
                        </span>
                      </div>
                      <div className="SubP-feature-item">
                        <span className={`SubP-feature-icon ${plan.features.verification_badge ? 'SubP-included' : 'SubP-excluded'}`}>
                          {plan.features.verification_badge ? '✓' : '✕'}
                        </span>
                        <span className="SubP-feature-name">
                          {t('payments.feature_items.verification_badge')}
                        </span>
                      </div>
                      <div className="SubP-feature-item">
                        <span className={`SubP-feature-icon ${plan.features.create_groups ? 'SubP-included' : 'SubP-excluded'}`}>
                          {plan.features.create_groups ? '✓' : '✕'}
                        </span>
                        <span className="SubP-feature-name">
                          {plan.features.create_groups 
                            ? (plan.features.create_groups === true || plan.features.create_groups >= 999 
                                ? t('payments.feature_items.create_groups') 
                                : t('payments.feature_items.create_up_to_groups', { count: plan.features.create_groups })) 
                            : t('payments.feature_items.create_groups')}
                        </span>
                      </div>
                      {/* Show advanced features only for higher tiers */}
                      {(plan.name === 'Premium' || plan.name === 'Business') && (
                        <>
                          <div className="SubP-feature-item">
                            <span className={`SubP-feature-icon ${plan.features.advanced_stats ? 'SubP-included' : 'SubP-excluded'}`}>
                              {plan.features.advanced_stats ? '✓' : '✕'}
                            </span>
                            <span className="SubP-feature-name">
                              {t('payments.feature_items.advanced_statistics')}
                            </span>
                          </div>
                          <div className="SubP-feature-item">
                            <span className={`SubP-feature-icon ${plan.features.co2_calculator ? 'SubP-included' : 'SubP-excluded'}`}>
                              {plan.features.co2_calculator ? '✓' : '✕'}
                            </span>
                            <span className="SubP-feature-name">
                              {t('payments.feature_items.co2_calculator')}
                            </span>
                          </div>
                        </>
                      )}
                      {/* Business-only features */}
                      {plan.name === 'Business' && (
                        <>
                          <div className="SubP-feature-item">
                            <span className="SubP-feature-icon SubP-included">✓</span>
                            <span className="SubP-feature-name">
                              {t('payments.feature_items.premium_support')}
                            </span>
                          </div>
                          <div className="SubP-feature-item">
                            <span className="SubP-feature-icon SubP-included">✓</span>
                            <span className="SubP-feature-name">
                              {t('payments.feature_items.branded_profile')}
                            </span>
                          </div>
                          <div className="SubP-feature-item">
                            <span className="SubP-feature-icon SubP-included">✓</span>
                            <span className="SubP-feature-name">
                              {t('payments.feature_items.revenue_sharing')}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
              
              {(!userSubscription || 
                (userSubscription.plan_id !== plan.id && 
                 userSubscription.subscription_plans?.name !== plan.name)) && (
                <div className="SubP-stripe-buy-button-container">
                  {/* Basic Plan - Show claimed status (everyone should have it now) */}
                  {plan.name === 'Basic' && (
                    <div className="SubP-claimed-badge">
                      🎉 {t('payments.soft_launch.claimed_badge') || 'Active - Free During Soft Launch!'}
                    </div>
                  )}
                  
                  {/* Premium and Business Plans - Coming Soon */}
                  {(plan.name === 'Premium' || plan.name === 'Business') && (
                    <button 
                      className="SubP-subscribe-button SubP-coming-soon"
                      onClick={() => handleSubscribe(plan)}
                      disabled={false}
                    >
                      {t('payments.soft_launch.coming_soon_button')}
                    </button>
                  )}
                  
                  {/* Other plans (if any) - Normal subscription */}
                  {plan.name !== 'Basic' && plan.name !== 'Premium' && plan.name !== 'Business' && (
                    <button 
                      className="SubP-subscribe-button"
                      onClick={() => handleSubscribe(plan)}
                      disabled={isProcessing || !user}
                    >
                      {isProcessing ? <Spinner size="small" /> : t('payments.subscribe_now')}
                    </button>
                  )}
                  
                  {!user && (
                    <div className="SubP-login-overlay">
                      <button 
                        className="SubP-login-button"
                        onClick={() => navigate('/login')}
                      >
                        {t('auth.login_to_subscribe')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <ChatV2/>
    </>
  );
};

export default SubscriptionPlans;